import {
  RouteNavigationAction,
  SdContinueClientFlowAction,
  VxlButtonStyle,
  ZelleSsoTokenAccountIneligibleError,
  ZelleSsoTokenMuling,
  ZelleSsoTokenScams,
} from '@varo/graphql-schema-types';
import type { ZelleSSOTokenError } from 'features/moveMoney/hooks/useZelleSSOToken';
import { createPartialMock } from 'services/UrqlClient/exchanges/mockExchange/utils/mockUtils';

export const mockZelleScamsWarningResponse = {
  zelleSSOToken: createPartialMock<ZelleSsoTokenScams>({
    __typename: 'ZelleSSOTokenScams',
    id: 'mock-zelle-scams-warning-1',
    title: 'Stay protected from scams',
    subtitle:
      'Only send money to people you know and trust. To spot scams, watch out for:',
    items: [
      'Messages pretending to be your bank',
      'Requests to pay for fake items or services',
      'Urgent asks for money, even from someone you know',
    ],
  }),
};

export const mockZelleAccountIneligibleResponse = {
  zelleSSOToken: createPartialMock<ZelleSsoTokenAccountIneligibleError>({
    __typename: 'ZelleSSOTokenAccountIneligibleError',
    isZelleLogo: true,
    title: 'Your access to Zelle® is subject to account activity',
    subtitle:
      "You'll be able to use Zelle® once your account meets these requirements:",
    ineligibleTitle: 'Limited Zelle® access — account activity required',
    ineligibleSubtitle: 'Meet the requirements below to restore full access:',
    items: [
      '$1,000+ in total deposits',
      '$200 deposited in the last 61 days',
      '60+ days of healthy account activity',
    ],
    requirements: [
      {
        __typename: 'ZelleIneligibleRequirement',
        id: 'req-1',
        desc: '$1,000+ in total deposits',
        isMet: false,
      },
      {
        __typename: 'ZelleIneligibleRequirement',
        id: 'req-2',
        desc: '$200 deposited in the last 61 days',
        isMet: false,
      },
      {
        __typename: 'ZelleIneligibleRequirement',
        id: 'req-3',
        desc: '60+ days of healthy account activity',
        isMet: true,
      },
    ],
    button: {
      __typename: 'ButtonComponentV2',
      id: 'zelle-ineligible-manage-direct-deposit',
      buttonText: 'Manage direct deposit',
      buttonStyle: VxlButtonStyle.Primary,
      buttonOnClick: createPartialMock<RouteNavigationAction>({
        __typename: 'RouteNavigationAction',
        route: '/move-money/direct-deposit',
      }),
    },
    displayAnalytics: {
      __typename: 'AnalyticsEvent',
      name: 'Screen Viewed',
      properties: [
        {
          __typename: 'AnalyticsEventProperty',
          key: 'screen_name',
          value: 'Zelle Failed',
        },
        {
          __typename: 'AnalyticsEventProperty',
          key: 'decline_reason',
          value: 'qualification_lost_eligibility',
        },
      ],
    },
    ineligibleDisclaimer:
      'Eligibility is at the discretion of Varo and may change at any time.',
    disclaimer: {
      __typename: 'HtmlComponent',
      id: 'zelle-ineligible-disclaimer',
      html: "Eligibility is at the discretion of Varo and may change at any time. See <a href='terms-and-conditions-link'>Terms and Conditions</a> for more details.",
      textStyle: null,
      linkStyle: null,
      boldStyle: null,
      linkActions: [
        {
          __typename: 'OpenPdfUrlAction',
          id: 'terms-and-conditions-link',
          url: 'https://uat-cdn.varomoney.com/agreements/8036f86b-fb11-4a1c-b4a9-3bb590c21aa8.pdf',
        },
      ],
    },
  }),
};

export const mockZelleRiskIneligibleResponse = {
  zelleSSOToken: createPartialMock<ZelleSsoTokenAccountIneligibleError>({
    __typename: 'ZelleSSOTokenAccountIneligibleError',
    isZelleLogo: false,
    title: 'Your access to Zelle® has changed',
    subtitle:
      'Zelle® is not available at this time. Access is based on ongoing account verification and continuing to meet activity requirements.',
    ineligibleTitle: 'Zelle® access has been restricted',
    ineligibleSubtitle: 'Zelle® is currently unavailable on your account.',
    items: [],
    requirements: [],
    button: {
      __typename: 'ButtonComponentV2',
      id: 'zelle-ineligible-got-it',
      buttonText: 'Got it',
      buttonStyle: VxlButtonStyle.Primary,
      buttonOnClick: createPartialMock<SdContinueClientFlowAction>({
        __typename: 'SDContinueClientFlowAction',
        id: 'zelle-ineligible-got-it',
      }),
    },
    displayAnalytics: {
      __typename: 'AnalyticsEvent',
      name: 'Screen Viewed',
      properties: [
        {
          __typename: 'AnalyticsEventProperty',
          key: 'screen_name',
          value: 'Zelle Failed',
        },
        {
          __typename: 'AnalyticsEventProperty',
          key: 'decline_reason',
          value: 'fraud_prevention_never_eligible',
        },
      ],
    },
    disclaimer: {
      __typename: 'HtmlComponent',
      id: 'zelle-ineligible-disclaimer',
      html: 'Eligibility is at the discretion of Varo and may change at any time.',
      textStyle: null,
      linkStyle: null,
      boldStyle: null,
      linkActions: [],
    },
  }),
};

export const mockZelleMulingWarningResponse = {
  zelleSSOToken: createPartialMock<ZelleSsoTokenMuling>({
    __typename: 'ZelleSSOTokenMuling',
    id: 'mock-zelle-muling-warning-1',
    title: "Don't move money for someone else",
    subtitle:
      'Scammers often ask people to receive or transfer money on their behalf. Even if it seems harmless, this activity can result in account closure and possible legal action.',
    disclaimer:
      'You may lose access to Zelle® if we detect this type of activity.',
  }),
};

export const mockZelleErrorResponse = {
  zelleSSOToken: createPartialMock<ZelleSSOTokenError>({
    __typename: 'ZelleSSOTokenError',
    code: 'ZELLE_ERROR_001',
    errorTitle: "This page isn't loading right now",
    message: "We're working on it.",
  }),
};
